/**
 * Envío AP-4 · «Editar imagen» en el timeline: se edita sobre el preview,
 * en el lugar y tamaño del clip; el panel de herramientas ocupa la ventana X.
 * Al aplicar, se sube una imagen NUEVA (Galería) y el clip pasa a usarla;
 * el original no se toca y se puede volver a él.
 */
import { useEffect } from 'react';
import { Loader2, PenLine, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import useTimelineStore from '../../store/timelineStore';
import useImageEditStore from '../../store/imageEditStore';
import { useEditableImage } from './useEditableImage';
import ImageEditStage from './ImageEditStage';
import ImageEditPanel from './ImageEditPanel';
import { saveEditedImage } from './imageEditActions';
import { describeEdit, needsTransparency, outputSize } from '../../utils/imageEdit';
import '../../styles/image-edit.css';

const KEYS = { video: 'videoClips', image: 'imageClips', v1: 'overlayV1Clips', v2: 'overlayV2Clips' };
const isBase = (trackKind) => trackKind === 'video' || trackKind === 'image';

export const findClip = (state, target) => (target && KEYS[target.trackKind]
  ? (state[KEYS[target.trackKind]] || []).find((c) => c.id === target.id) || null : null);

/** Lo que se edita: siempre el original, con los ajustes que ya tenía. */
export const editSourceOf = (clip) => (clip?.edit?.original?.source_url
  ? { url: clip.edit.original.source_url, label: clip.edit.original.label || clip.label, params: clip.edit.params }
  : { url: clip?.source_url, label: clip?.label, params: null });

export function requestImageEdit(trackKind, id) {
  return useTimelineStore.getState().startImageEdit(trackKind, id);
}

export function cancelImageEdit() {
  useTimelineStore.getState().stopImageEdit();
  useImageEditStore.getState().close();
}

export async function applyTimelineImageEdit() {
  const tl = useTimelineStore.getState();
  const ie = useImageEditStore.getState();
  const target = tl.imageEdit;
  const clip = findClip(tl, target);
  if (!clip || !ie.img) return;
  ie.setSaving(true);
  const base = isBase(target.trackKind);
  const src = editSourceOf(clip);
  try {
    const params = ie.params;
    const out = await saveEditedImage({ img: ie.img, params, label: src.label, background: base ? '#000000' : null });
    useTimelineStore.getState().applyImageEdit(target.trackKind, target.id, { ...out, params });
    cancelImageEdit();
    const name = out.name.replace(/\.png$/, '');
    toast.success('Imagen nueva guardada en tu Galería', {
      description: `«${name}» ya está en el clip. El original no cambió.${
        base && needsTransparency(params) ? ' En la pista base lo transparente se ve negro.' : ''}`,
      position: 'bottom-center',
      duration: 8000,
      action: { label: 'Deshacer', onClick: () => useTimelineStore.getState().undoLast() },
      cancel: { label: 'Ver en Galería', onClick: () => window.dispatchEvent(new CustomEvent('sml:timeline-open-gallery')) },
    });
  } catch (err) {
    useImageEditStore.getState().setSaving(false);
    toast.error(err?.userMessage || 'No se pudo guardar la imagen editada. Probá de nuevo.');
  }
}

/** Abre la sesión cuando cambia el clip a editar (y la cierra al salir). */
function useTimelineSession(target, clip) {
  const src = editSourceOf(clip);
  const key = target ? `${target.trackKind}:${target.id}` : null;
  useEffect(() => {
    if (!key || !src.url) return undefined;
    useImageEditStore.getState().open({ key, source: { url: src.url, label: src.label }, origin: 'timeline', target }, src.params);
    return () => useImageEditStore.getState().close();
    // eslint-disable-next-line
  }, [key]);
  return src;
}

/**
 * La caja de edición sobre el preview, donde está el clip: superposición con
 * su posición y escala (sin giro mientras se edita), o la pista base centrada
 * como se ve en el video.
 */
export function TimelineImageEditBox({ clip, trackKind, stage, layerZ = 60 }) {
  const img = useImageEditStore((s) => s.img);
  const loadError = useImageEditStore((s) => s.loadError);
  const rotate = useImageEditStore((s) => s.params.rotate);

  let width;
  let left = '50%';
  let top = '50%';
  if (isBase(trackKind)) {
    const o = img ? outputSize(img.naturalWidth, img.naturalHeight, rotate, 2000) : { width: 16, height: 9 };
    const a = o.width / o.height;
    const sa = (stage?.width || 16) / (stage?.height || 9);
    width = `${(a >= sa ? 1 : a / sa) * 100}%`;
  } else {
    const t = clip.transform || {};
    left = `${(t.x_pct ?? 0.5) * 100}%`;
    top = `${(t.y_pct ?? 0.5) * 100}%`;
    width = `${(t.scale ?? 0.4) * 100}%`;
  }
  return (
    <div className="ie-tl-box" style={{ left, top, width, zIndex: layerZ }} data-testid="ie-tl-box"
      onMouseDown={(e) => e.stopPropagation()}>
      {img ? <ImageEditStage img={img} /> : (
        <div className="ie-tl-wait">
          {loadError ? <span role="alert">{loadError}</span> : <><Loader2 size={16} className="ie-spin" aria-hidden="true" /> Abriendo…</>}
        </div>
      )}
    </div>
  );
}

/** El panel de herramientas en la ventana X mientras se edita. */
export function TimelineImageEditPanel() {
  const target = useTimelineStore((s) => s.imageEdit);
  const clip = useTimelineStore((s) => findClip(s, s.imageEdit));
  const saving = useImageEditStore((s) => s.saving);
  const img = useImageEditStore((s) => s.img);
  const src = useTimelineSession(target, clip);
  const { img: loaded, error } = useEditableImage(clip ? src.url : null);
  useEffect(() => { useImageEditStore.getState().setImg(loaded || null, error); }, [loaded, error]);
  useEffect(() => { if (target && !clip) cancelImageEdit(); }, [target, clip]);
  if (!clip) return null;
  const base = isBase(target.trackKind);
  return (
    <ImageEditPanel
      className="ie-panel-x"
      sourceLabel={src.label}
      imgSize={img ? { w: img.naturalWidth, h: img.naturalHeight } : undefined}
      onCancel={() => { if (!saving) cancelImageEdit(); }}
      onApply={applyTimelineImageEdit}
      applying={saving || !img}
      note={<>Al aplicar se crea una <b>imagen nueva</b> en tu Galería y el clip pasa a usarla. <b>El original no cambia</b> y podés volver a él cuando quieras.{base ? ' En la pista base, lo transparente se ve negro.' : ''}</>}
    />
  );
}

/** Tarjeta en el panel del clip: editar / volver a editar / restablecer. */
export function ImageEditCard({ clip, trackKind }) {
  const editing = useTimelineStore((s) => !!s.imageEdit);
  const isImage = !!clip && trackKind !== 'audio' && (clip.kind || (trackKind === 'image' ? 'image' : 'video')) === 'image';
  if (!isImage || editing) return null;
  const edited = !!clip.edit?.original?.source_url;
  const summary = edited ? describeEdit(clip.edit.params) : '';
  return (
    <section className={`ie-card ${edited ? 'is-edited' : ''}`} data-testid="ie-card">
      {edited && (
        <p className="ie-card-txt">
          <b>Imagen editada</b>
          <span>Basada en «{clip.edit.original.label || 'la original'}»{summary ? ` · ${summary}` : ''}</span>
        </p>
      )}
      <button type="button" className="ie-btn accent" onClick={() => requestImageEdit(trackKind, clip.id)} data-testid="ie-card-edit">
        <PenLine size={15} aria-hidden="true" /> {edited ? 'Volver a editar' : 'Editar imagen'}
      </button>
      {edited && (
        <>
          <button type="button" className="ie-btn ghost" data-testid="ie-card-restore"
            onClick={() => {
              if (!useTimelineStore.getState().restoreImageOriginal(trackKind, clip.id)) return;
              toast('Se restableció el original', {
                description: 'La versión editada sigue en tu Galería.',
                position: 'bottom-center',
                action: { label: 'Deshacer', onClick: () => useTimelineStore.getState().undoLast() },
              });
            }}>
            <RotateCcw size={14} aria-hidden="true" /> Restablecer el original
          </button>
          <small className="ie-card-hint">«Volver a editar» parte del original con los ajustes guardados, así nada se degrada al editar varias veces.</small>
        </>
      )}
    </section>
  );
}
